import { BaseButton } from "@/components/atom/button/BaseButton";
import { FormDescription } from "@/components/atom/title/form/FormDescription";
import { FormSubTitle } from "@/components/atom/title/form/FormSubTitle";
import { FormTitle } from "@/components/atom/title/form/FormTitle";
import { MantineDataTable } from "@/components/molecule/mantineDataTable/MantineDataTable";
import { ItemType } from "@/components/organism/nonStandard/NonStandardTable";
import FormTemplate from "@/components/template/form/FormTemplate";
import { COMMON_TEXT } from "@/constants/text";
import { useModal } from "@/context/ModalStackManager";
import { useInventoriesFetch } from "@/fetch/page/work/useInventoriesFetch";
import { useWorkFetch } from "@/fetch/page/work/useWorkFetch";
import { useDiligenceHandler } from "@/hooks/handler/useDiligenceHandler";
import { usePerformanceHandler } from "@/hooks/handler/usePerformanceHandler";
import { useNotification } from "@/hooks/useNotification";
import { useNonStandardStore } from "@/store/nonStandard.store";
import { Flex } from "@mantine/core";
import { ProductionPlansGet200ResponseRowsInnerWorksInner } from "@sizlcorp/sizl-api-document/dist/models";
import { MRT_ColumnDef } from "mantine-react-table";
import { useMemo } from "react";

const NonStandardComplete = Object.assign(
  {},
  {
    Template: FormTemplate,
    Title: FormTitle,
    Description: FormDescription,
    SubTitle: FormSubTitle,
    Button: BaseButton,
    Table: MantineDataTable,
  },
);

export const NonStandardCompleteForm = () => {
  const { data, selectedRowIds, setSelectedRowIds } = useNonStandardStore();
  const { closeModal } = useModal();
  const { data: work } = useWorkFetch();
  const workData = work?.data;
  const { data: inventory } = useInventoriesFetch({
    workData: workData as ProductionPlansGet200ResponseRowsInnerWorksInner,
  });
  const { notifyRawMaterialAdjustment } = useNotification();
  const { handleGroupPerformance, handleGroupFakeDefect } =
    usePerformanceHandler();
  const { onSubmit } = useDiligenceHandler();

  const selectedRow = selectedRowIds?.map((id) =>
    data.find((value) => value.id === id),
  );

  const inventories = inventory?.data;
  const lastInventoryLength = (inventories as { length: number }).length - 1;

  const calculateConsumedMaterial = (
    selectedRow: (ItemType | undefined)[] | undefined,
  ) => {
    // 예상 소모 원자재량 = (체크한 실적 수량) * routingBoms[0].quantity
    // 체크한 실적 수량의 기준: 로컬스토리지 값 -> store의 row.end -> 초기 기본값(목표-등록)
    const sum =
      selectedRow
        ?.map((row) => {
          if (!row) return 0;

          const id = row.id as number;
          const performance = Number(row.summary?.performance ?? 0);
          const target = Number(row.targetQuantity ?? 0);

          // 1) 로컬 저장값 우선
          let effectiveEnd: number | undefined = undefined;
          try {
            const raw = window.localStorage.getItem(`tempWork_${id}`);
            if (raw) {
              const parsed = JSON.parse(raw);
              const v = parsed?.end;
              if (v !== undefined && v !== null && v !== "") {
                const num = Number(v);
                if (!Number.isNaN(num)) effectiveEnd = num;
              }
            }
          } catch {}

          // 2) store의 값 사용
          if (effectiveEnd === undefined) {
            const v = row.end;
            if (v !== undefined && v !== null && v !== "") {
              const num = Number(v);
              if (!Number.isNaN(num)) effectiveEnd = num;
            }
          }

          // 3) 초기 기본값(목표-등록)
          if (effectiveEnd === undefined) {
            effectiveEnd = Math.max(0, target - performance);
          }

          const endQuantity = Number(effectiveEnd ?? 0);
          let bomQty = Number(
            row?.routingData?.routingBoms?.[0]?.quantity ?? 0,
          );
          if (Number.isNaN(bomQty)) bomQty = 0;
          return endQuantity * bomQty;
        })
        .reduce((acc, cur) => acc + cur, 0) ?? 0;

    return sum;
  };

  const isButtonDisabled =
    Number(inventories?.[lastInventoryLength]?.quantity ?? 0) <
    calculateConsumedMaterial(selectedRow);

  const convertedData = [
    {
      remainRawMaterial: inventories?.[lastInventoryLength]?.quantity
        ? Number(inventories?.[lastInventoryLength]?.quantity).toFixed(3)
        : "-",
      lotNumber: inventories?.[lastInventoryLength]?.lot?.name ?? "-",
      expectedConsumedRawMaterial:
        calculateConsumedMaterial(selectedRow).toFixed(3),
    },
  ];

  const columns = useMemo<MRT_ColumnDef[]>(
    () => [
      {
        accessorKey: "remainRawMaterial",
        header: COMMON_TEXT.COLUMN.REMAIN_RAW_MATERIAL,
      },
      {
        accessorKey: "lotNumber",
        header: COMMON_TEXT.COLUMN.LOT_NUMBER,
      },
      {
        accessorKey: "expectedConsumedRawMaterial",
        header: COMMON_TEXT.COLUMN.EXPECTED_CONSUMED_RAW_MATERIAL,
      },
    ],
    [],
  );

  // 실적 수량 저장하는 문제에 대해 논의해봐야겠다.

  const handleProductionInput = () => {
    if (!selectedRow) return;

    const workId = selectedRow.map((value) => value?.id) as number[];
    const ids = data.map((value) => value.id) as number[];
    const groupWorkId = workId.map(() => ids);
    const quantity = selectedRow.map((value) => {
      const end = Number(value?.end ?? 0);
      const performance = Number(value?.summary?.performance ?? 0);
      const todoQuantity = Number(value?.summary?.todoQuantity ?? 0);
      const total =
        value?.end !== undefined
          ? end + performance
          : todoQuantity + performance;
      return total.toString();
    }) as string[];

    const warnInsufficiencies = selectedRow.map(() => true);
    const defectCount = selectedRow.map(
      (value) => value?.defectCount?.toString() ?? "0",
    );
    const priorityConsume = selectedRow.map(() => true);

    handleGroupPerformance({
      workIds: workId,
      accQuantities: quantity,
      warnInsufficiencies: warnInsufficiencies,
      priorityConsume: priorityConsume, // 투입한거 우선소모 boolean
      groupWorkId: groupWorkId,
    }).then(() => {
      const hasDefects = defectCount.some((value) => value !== "0");
      if (hasDefects) {
        handleGroupFakeDefect({
          workIds: workId,
          quantities: defectCount,
        });
      }
      setSelectedRowIds([]); // 작업 종료 후 선택된 행 초기화
    });
    closeModal({});
  };

  const handleRawMaterialAdjustment = async () => {
    const result = await notifyRawMaterialAdjustment();

    if (result) {
      await onSubmit({
        wmsDiligencePostRequest: {
          itemCode: inventories?.[lastInventoryLength]?.itemCode as string,
          locationCode: inventories?.[lastInventoryLength]
            ?.locationCode as string,
          quantity: String(calculateConsumedMaterial(selectedRow)),
          lotId: inventories?.[lastInventoryLength]?.lotId,
          lotInfo: {
            name: inventories?.[lastInventoryLength]?.lot?.name,
            expiration: inventories?.[lastInventoryLength]?.lot?.expiration,
          },
          actionFrom: "POP",
          erpSync: true,
        },
      });

      handleProductionInput();
    }
  };

  return (
    <NonStandardComplete.Template isButtonDisabled={true}>
      <NonStandardComplete.Table
        mantineTableHeadCellProps={{
          sx: {
            backgroundColor: "#FFFFFF",
          },
        }}
        mantineTableBodyCellProps={{
          style: {
            backgroundColor: "#FFFFFF",
            color: "#000000",
          },
        }}
        columns={columns}
        data={convertedData}
      />
      <NonStandardComplete.Description>
        *{COMMON_TEXT.DESCRIPTION.PRODUCTION_INPUT_DESC}
      </NonStandardComplete.Description>
      <NonStandardComplete.Description>
        *{COMMON_TEXT.DESCRIPTION.RAW_MATERIAL_ADJUSTMENT_AND_INPUT_DESC}
      </NonStandardComplete.Description>
      <NonStandardComplete.SubTitle
        c="green"
        align="center"
        size={"1.2rem"}
        fw={"bold"}
      >
        {COMMON_TEXT.DESCRIPTION.PRODUCTION_INPUT_SAVE_DESC}
      </NonStandardComplete.SubTitle>
      <Flex justify={"space-between"}>
        <NonStandardComplete.Button
          onClick={() => closeModal({})}
          variant="outline"
          color="gray"
        >
          {COMMON_TEXT.BUTTON.CANCEL}
        </NonStandardComplete.Button>
        <Flex gap={"md"}>
          <NonStandardComplete.Button
            disabled={isButtonDisabled}
            color="cyan"
            onClick={handleProductionInput}
          >
            {COMMON_TEXT.BUTTON.PRODUCTION_INPUT}
          </NonStandardComplete.Button>
          <NonStandardComplete.Button
            color="indigo"
            onClick={handleRawMaterialAdjustment}
          >
            {COMMON_TEXT.BUTTON.RAW_MATERIAL_ADJUSTMENT_AND_INPUT}
          </NonStandardComplete.Button>
        </Flex>
      </Flex>
    </NonStandardComplete.Template>
  );
};
